"use client";

import { useLinkStatus } from "next/link";

/**
 * Renders inside the job row's whole-card `<Link>` and tints the row while that
 * navigation is in flight.
 *
 * Why this exists: of 181 people who tapped a job card in a 30-day window, only
 * 156 reached a rendered detail page. The App Router keeps the current page
 * painted until the destination's RSC payload arrives, so on a template with a
 * measured INP p75 of 1.1-2.3s the tap produced no visible change at all and 25
 * committed users left in the gap.
 *
 * Why `useLinkStatus` rather than the two obvious alternatives:
 *
 * - A route-segment `loading.tsx` streams a 200 shell before the page's
 *   `notFound()` resolves, turning real 404s into soft-404s
 *   (`pnpm check:perf-hygiene`).
 * - Driving the navigation with `useRouter` + `startTransition` from the row
 *   means `<JobRow>` needs a mounted app router, which it does not have in
 *   component tests — and, more importantly, it means reimplementing
 *   modifier-click handling that `<Link>` already gets right.
 *
 * This hook reports the pending state of the enclosing `<Link>` without taking
 * over its navigation.
 */
export function CardNavPending() {
	const { pending } = useLinkStatus();
	if (!pending) return null;
	return (
		<span
			aria-hidden="true"
			data-testid="card-nav-pending"
			className="absolute inset-0 bg-muted/50 transition-opacity"
		/>
	);
}
